import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as rangeUtils from './range'
import { SliderInput, type SliderChangeEvent } from './SliderInput'

type RangeInputProps = {
  value: rangeUtils.Value
  boundary: rangeUtils.Boundary
  onChange: (value: rangeUtils.Value) => void
}

export function RangeInput({ value, boundary, onChange }: RangeInputProps): React.ReactElement {
  const roundBoundary: rangeUtils.Boundary = {
    min: Math.floor(boundary.min),
    max: Math.ceil(boundary.max),
  }

  const [range, setRange] = React.useState(rangeUtils.sanitize(value, roundBoundary))

  React.useEffect(() => {
    const sanitized = rangeUtils.sanitize(value, roundBoundary)
    if (range.start !== sanitized.start || range.end !== sanitized.end) {
      setRange(sanitized)
    }
  }, [value])

  return (
    <>
      <SliderInputWithMargin boundary={roundBoundary} value={range} onChange={handleSlideChange} />
      <FlexWrapper>
        <plume.TextField
          label="Min €"
          value={range.start}
          onBlur={handleTextEvent('start')}
          onChange={handleTextEvent('start')}
          onKeyDown={handleKeyDown}
          min={roundBoundary.min}
          max={roundBoundary.max}
          type="text"
          key={'range-start'}
        />
        <Separator>-</Separator>
        <plume.TextField
          label="Max €"
          value={range.end}
          onBlur={handleTextEvent('end')}
          onChange={handleTextEvent('end')}
          onKeyDown={handleKeyDown}
          min={range.start}
          max={roundBoundary.max}
          type="text"
          key={`range-end`}
        />
      </FlexWrapper>
    </>
  )

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.currentTarget.blur()
    }
  }

  function handleSlideChange(event: SliderChangeEvent) {
    setRange((range) => {
      if (event.dispatch) {
        onChange(event.value)
      }
      // skip updating the state if the range did not actually change
      if (event.value.start === range.start && event.value.end === range.end) {
        return range
      }
      return event.value
    })
  }

  function handleTextEvent(type: keyof rangeUtils.Value) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value
      const numberValue = Number(event.currentTarget.value)
      setRange((range) => {
        const nextRange = { ...range, [type]: Number.isNaN(numberValue) || value === '' ? '' : numberValue }

        // dispatch change on blur (even if range did not change, because change
        // already happen on last `change` event)
        if (event.type === 'blur') {
          onChange(nextRange)
        }

        // skip updating the state if the range did not actually change
        if (nextRange[type] === range[type]) {
          return range
        }

        return nextRange
      })
    }
  }
}

const SliderInputWithMargin = styled(SliderInput)`
  margin: 40px 0 0;
  padding-bottom: 40px;
`

const Separator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  top: 10px;
`

const FlexWrapper = styled.div`
  display: grid;
  grid-template-columns: 45% 10% 45%;
`
